import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Row,
  Col,
  Container,
  PopoverBody,
  Popover,
  Input,
  Label,
} from 'reactstrap';
import { Filter } from 'react-feather';
import CommonService from 'services/CommonService';
import { CustomDatePicker } from 'components/shared/CustomDatePicker';
import moment from 'moment';
import CustomLoader from 'components/Common/CustomLoader';
import UseDebounce from 'components/shared/UseDebounce';
import { IColumnModel } from 'models/IColumnModel';
import { ISearchModel } from 'models/ISearch.Model';
import CustomDataTable from 'components/shared/CustomDataTable';
import { CustomButton } from 'components/shared/CustomButton';

interface AuditEntity {
  Id: number;
  AuditTypeId: any;
  AuditTypeName: string;
  AuditTypeEnumName: string;
  ActionId: number;
  AuditJSON: string | null;
  CreatedBy: number;
  Created: string;
  ModifiedBy: number;
  Modified: string;
  Active: boolean;
  FirstName: string | null;
  TotalRowsCount: any;
}

function OrderSummaryView() {
  const [audits, setAudits] = useState<Array<any>>([]);
  const [error, setError] = useState<string | null>(null);
  const [createdByList, setCreatedByList] = useState<{ Id: number; Name: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isload, setIsLoad] = useState(false);
  const [searchText, setSearchText] = useState<any>();
  const debouncedSearch = UseDebounce(searchText, 1000);
  const [rowsPerPage] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<any>(1);
  const [offsetStart, setOffsetStart] = useState<number>(0);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const togglePopover = () => setPopoverOpen(!popoverOpen);

  const columns: IColumnModel[] = useMemo(() => [
    {
      Header: 'Action By',
      accessor: 'FirstName',
      width: "10%",
      displayInList: true,
      Filter: true,
    },
    {
      Header: 'Screen Name',
      accessor: 'AuditTypeName',
      displayInList: true,
      width: "15%",
      Filter: false,
    },
    {
      Header: 'Action',
      accessor: (row: any) =>
        row.ActionId?.length > 20
          ? row.ActionId.substring(0, 30) + "..."
          : row.ActionId,
      width: "15%",
      displayInList: true,
      Filter: false
    },
    {
      Header: 'ActionDate',
      accessor: 'Created',
      displayInList: true,
      width: "15%",
      Filter: false,
      datatype: "dateWithBadge",
    }, {
      Header: 'Changes',
      accessor: (row: any) =>
        row.Action?.length > 70
          ? row.Action.substring(0, 80) + "..."
          : row.Action,
      displayInList: true,
      width: "15%",
      Filter: false,
    }, {
      Header: 'Detail After',
      accessor: (row: any) =>
        row.AuditJSONAfter?.length > 20
          ? row.AuditJSONAfter.substring(0, 30) + "..."
          : row.AuditJSONAfter,
      width: "15%",
      displayInList: true,
      Filter: false
    }, {
      Header: 'Detail Before',
      accessor: (row: any) =>
        row.AuditJSONBefore?.length > 20
          ? row.AuditJSONBefore.substring(0, 30) + "..."
          : row.AuditJSONBefore,
      width: "15%",
      displayInList: true,
      Filter: false
    },
  ], []);

  const handleFromDateChange = (value: any) => {
    setFromDate(value)
  }

  const handleToDateChange = (value: any) => {
    setToDate(value)
  }

  const searchByText = (pageNumber: any, searchText: any, selectedUserId: any, sortcolumn?: string, sortorder?: string) => {
    setIsLoad(true);
    let searchModel: ISearchModel = {
      UserId: selectedUserId,
      offsetStart: offsetStart,
      rowsPerPage: rowsPerPage,
      sortOrder: "desc",
      sortOrderColumn: "created",
      searchText: searchText,
      FromDate: fromDate,
      ToDate: toDate,
    }

    CommonService.post("auditLog", "GetAuditLogByUserId", searchModel).then(res => {
      if (res.status == 200) {
        let data: AuditEntity[] = res.data;
        setAudits(data);
        setTotalRecords(data[0]?.TotalRowsCount);
        setIsLoad(false);
      }
    });
  }

  const getUserNameList = () => {
    CommonService.postWithOutData("auditLog", "GetAuditLogCreatedByUsers").then(res => {
      if (res.status == 200) {
        let data = res.data;
        setCreatedByList(data);
      }
    });
  }

  const searchHandler = (searchKeyword: any) => {
    setSearchText(searchKeyword);
    searchByText(0, searchKeyword, selectedUserId);
  }

  const onColumnSortChange = (columnName, sortorder) => {
    setSelectedColumn(columnName);
    setSortOrder(sortorder);
  };

  const pagination = (pageNumber: any) => {
    searchByText(pageNumber, debouncedSearch, selectedUserId, selectedColumn, sortOrder);
  }

  useEffect(() => {
    searchByText(0, debouncedSearch, selectedUserId);
  }, [offsetStart, rowsPerPage, selectedUserId, fromDate, toDate]);

  useEffect(() => {
    getUserNameList();
  }, []);

  return (
    <div className="page-content">
      <CustomLoader isOpen={isload} />
      <Container fluid className="p-0">
        <div className="d-flex justify-content-between">
          <h5 className="fw-semibold text-dofy dofy-admin-font-md mb-3" style={{ whiteSpace: "nowrap" }}>AUDITLOG</h5>
        </div>
        <div className='d-flex justify-content-end'>
          <Button id="FilterButton" color="primary" size="sm" className="d-flex align-items-center gap-1">
            <Filter size={14} />
            Filter
          </Button>
          <Popover
            placement="bottom-end"
            isOpen={popoverOpen}
            target="FilterButton"
            toggle={togglePopover}
            className="shadow-sm"
          >
            <PopoverBody style={{ minWidth: "250px" }}>
              <div className="mb-2">
                <Label for="CreatedBy" className="form-label">Action By</Label>
                <Input
                  type="select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : '')}
                >
                  <option value="">All Users</option>
                  {createdByList.map(user => (
                    <option key={user.Id} value={user.Id}>
                      {user.Name}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="mb-2">
                <CustomDatePicker
                  label={'From'}
                  value={fromDate ? moment(fromDate).format('YYYY-MM-DD') : ''}
                  onChange={(e: any) => handleFromDateChange(e.target.value)}
                  id={"FromDate"} error={error}
                />
              </div>
              <div className="mb-2">
                <CustomDatePicker
                  label={'To'}
                  value={toDate ? moment(toDate).format('YYYY-MM-DD') : ''}
                  onChange={(e: any) => handleToDateChange(e.target.value)}
                  id={"ToDate"} error={error}
                  disabled={!fromDate}
                  minValue={fromDate}
                />
              </div>
              <div className="d-flex justify-content-between mt-2">
                <CustomButton
                  type="button"
                  btnClassName="btn btn-sm "
                  name="Close"
                  onClick={() => {
                    togglePopover()
                  }}
                 iconClasssName={undefined} />
                <CustomButton
                  type="button"
                  btnClassName="btn btn-sm bg-danger"
                  name="Reset"
                  onClick={() => {
                    handleFromDateChange(null);
                    handleToDateChange(null);
                    setSelectedUserId('');
                  }}
                  disabled={!(fromDate || toDate || selectedUserId || selectedUserId === 0)} iconClasssName={undefined} />
              </div>
            </PopoverBody>
          </Popover>
        </div>
        <Row>
          <Col xl={12}>
            <CustomDataTable
              isPagination={audits[0]?.TotalRowsCount > 50 ? true : true}
              customPageSize={50}
              isActionEdit={false}
              isActionDelete={false}
              editHandler={undefined}
              isGlobalFilter={true}
              searchHandler={searchHandler}
              onColumnSortChange={onColumnSortChange}
              canShowButton1={false}
              selectedPage={pagination}
              canShowButton2={false}
              deleteHandler={undefined}
              createHandler={undefined}
              path={''}
              firstBtnName={''}
              specialGrid={false}
              recordsCount={audits[0]?.TotalRowsCount}
              pageSize={rowsPerPage}
              columns={columns}
              data={audits}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              offsetStart={offsetStart}
              setOffsetStart={setOffsetStart}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OrderSummaryView;